import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { AxiosError } from 'axios';

import { fetchOneGatheringRequest } from '~/api/residentGathering';
import { GatheringType } from '~/types/gathering';
import { getEventDuration } from '~/components/GatheringsTable/GatheringsTable';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spinner from '~/components/Spinner/Spinner';
import classes from './GatheringDetails.module.css';

// GroupEvent.commitType value for 'Interested' events - see GatheringForm.tsx commitTypes
const INTERESTED_COMMIT_TYPE_ID = '45890325-4e1d-4364-8aea-a13b2cf4c02c';

const GatheringDetails = () => {
  const { gatheringId } = useParams<{ gatheringId: string }>();
  const [gathering, setGathering] = useState<GatheringType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOneGatheringRequest(gatheringId)
      .then((res) => {
        setGathering(res.data);
      })
      .catch((e) => {
        const error = e as AxiosError;
        toast(error.message ?? 'Failed to load event details.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [gatheringId]);

  if (loading) return <Spinner />;
  if (!gathering) return null;

  const isOneTime = gathering.recurringTypeName === 'One Time';
  const dateTimeFormat = 'dddd, DD MMM YYYY, hh:mm A';
  const dateFormat = 'dddd, DD MMM YYYY';

  return (
    <div className={classes.container}>
      <Breadcrumbs />

      <div className={classes.header}>
        {gathering.image ? (
          <img
            className={classes.image}
            src={gathering.image as string}
            alt={gathering.title}
          />
        ) : (
          <div className={classes.imagePlaceholder} />
        )}
        <div className={classes.titleBlock}>
          <h1>{gathering.title}</h1>
          {gathering.cancelled && (
            <span className={classes.cancelled}>Cancelled</span>
          )}
          <p style={{ color: 'rgba(39, 33, 99, 0.6)', fontSize: '16px' }}>
            {gathering.groupName}
          </p>
        </div>
      </div>

      <hr className={classes.divider} />

      <div className={classes.infoGrid}>
        <div className={classes.infoItem}>
          <span className={classes.infoLabel}>
            {isOneTime ? 'Date & Time' : 'Start Date'}
          </span>
          <span className={classes.infoValue}>
            {isOneTime
              ? moment(gathering.scheduledStartTime)
                  .tz(gathering.buildingTimezone)
                  .format(dateTimeFormat)
              : moment(gathering.scheduledStartTime)
                  .tz(gathering.buildingTimezone)
                  .format(dateFormat)}
          </span>
        </div>

        {!isOneTime && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Start Time</span>
            <span className={classes.infoValue}>
              {moment(gathering.scheduledStartTime)
                .tz(gathering.buildingTimezone)
                .format('hh:mm A')}
            </span>
          </div>
        )}

        {gathering.scheduledEndTime && (
          gathering.isPartnerEvent ? (
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>
                {isOneTime ? 'End Date & Time' : 'End Time'}
              </span>
              <span className={classes.infoValue}>
                {isOneTime
                  ? moment(gathering.scheduledEndTime)
                      .tz(gathering.buildingTimezone)
                      .format(dateTimeFormat)
                  : moment(gathering.scheduledEndTime)
                      .tz(gathering.buildingTimezone)
                      .format('hh:mm A')}
              </span>
            </div>
          ) : (
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Duration</span>
              <span className={classes.infoValue}>
                {getEventDuration(
                  gathering.scheduledStartTime!,
                  gathering.scheduledEndTime
                )}
              </span>
            </div>
          )
        )}

        {!isOneTime && gathering.recurrentEndDate && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Recurring Until</span>
            <span className={classes.infoValue}>
              {moment(gathering.recurrentEndDate)
                .tz(gathering.buildingTimezone)
                .format(dateFormat)}
            </span>
          </div>
        )}

        {!isOneTime && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Frequency</span>
            <span className={classes.infoValue}>
              {gathering.recurringTypeName}
            </span>
          </div>
        )}

        {gathering.locationString && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Location</span>
            <span className={classes.infoValue}>{gathering.locationString}</span>
          </div>
        )}

        <div className={classes.infoItem}>
          <span className={classes.infoLabel}>RSVPs</span>
          <span className={classes.infoValue}>{gathering.rsvps ?? 0}</span>
        </div>

        {gathering.commitType !== INTERESTED_COMMIT_TYPE_ID && (gathering.maxGuests > 0 || gathering.maxEventAttendees > 0 || (gathering.isPartnerEvent && gathering.maxEventAttendees === null)) && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Capacity</span>
            <span className={classes.infoValue}>
              {gathering.isPartnerEvent && gathering.maxEventAttendees === null
                ? 'Unlimited'
                : gathering.maxEventAttendees > 0
                ? `${gathering.maxEventAttendees} attendees`
                : `${gathering.maxGuests} guests`}
            </span>
          </div>
        )}

        <div className={classes.infoItem}>
          <span className={classes.infoLabel}>Creator</span>
          <span className={classes.infoValue}>
            {gathering.creator.displayName}
          </span>
        </div>

        {gathering.isPartnerEvent && (gathering.partnerEventType || gathering.partnerOtherEventType) && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Event Type</span>
            <span className={classes.infoValue}>
              {gathering.partnerOtherEventType || gathering.partnerEventType}
            </span>
          </div>
        )}

        {gathering.isPartnerEvent && gathering.postalAddress?.addressLine1 && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Address</span>
            <span className={classes.infoValue}>
              {[
                gathering.postalAddress.addressLine1,
                gathering.postalAddress.addressLine2,
                gathering.postalAddress.addressLine3,
              ]
                .filter(Boolean)
                .join(', ')}
              {'\n'}
              {[
                gathering.postalAddress.locality,
                gathering.postalAddress.administrativeArea,
                gathering.postalAddress.postalCode,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </div>
        )}

        {gathering.isPartnerEvent && gathering.drivingDirections && (
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>Driving Directions</span>
            <span className={classes.infoValue}>{gathering.drivingDirections}</span>
          </div>
        )}
      </div>

      <hr className={classes.divider} />

      {gathering.description && (
        <div className={classes.section}>
          <p className={classes.sectionLabel}>Description</p>
          <p className={classes.sectionValue}>{gathering.description}</p>
        </div>
      )}

      {gathering.whatToBring && (
        <div className={classes.section}>
          <p className={classes.sectionLabel}>What to Bring</p>
          <p className={classes.sectionValue}>{gathering.whatToBring}</p>
        </div>
      )}

      {!isOneTime && gathering.upcomingEvents?.length > 0 && (
        <div className={classes.section}>
          <p className={classes.sectionLabel}>Upcoming Dates</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {gathering.upcomingEvents.slice(0, 6).map((event) => (
              <span
                key={event.uuid}
                style={{
                  padding: '6px 12px',
                  border: '1px solid rgb(39, 33, 99)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: 'rgb(39, 33, 99)'
                }}
              >
                {moment(event.scheduledStartTime)
                  .tz(gathering.buildingTimezone)
                  .format('ddd DD MMM YYYY')}
              </span>
            ))}
            {gathering.upcomingEvents.length > 6 && (
              <span style={{ fontSize: '14px', color: 'rgba(39, 33, 99, 0.6)', alignSelf: 'center' }}>
                +{gathering.upcomingEvents.length - 6} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GatheringDetails;
